import type { ReactNode } from 'react';
import { cn } from '@/lib/cn';
import { MicroLabel } from './micro-label';

export interface Column<T> {
  key: string;
  header: string;
  /** Tailwind width/alignment classes for this column. */
  className?: string;
  render: (row: T) => ReactNode;
}

/**
 * The hairline table.
 *
 * Deliberately plain: no zebra striping, no vertical rules, one hairline under each row, mono
 * uppercase headers. Sorting and filtering are server-side by contract (frontend.md section 2),
 * so this component renders and does not own data behaviour.
 */
export function DataTable<T>({
  columns,
  rows,
  rowKey,
  onRowClick,
  empty = 'Nothing to show yet.',
  footer,
  className,
  maxBodyHeight
}: {
  columns: Column<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  onRowClick?: (row: T) => void;
  empty?: ReactNode;
  footer?: ReactNode;
  className?: string;
  /**
   * Cap the scroll area in pixels, for a table whose length is a property of the data rather than
   * of the page -- a dataset's episodes can be sixty rows and push everything below it off-screen.
   * Opt-in, so a table whose height is already bounded by its query keeps growing with the page.
   */
  maxBodyHeight?: number;
}) {
  const scrolls = maxBodyHeight !== undefined;
  return (
    <div className={cn('overflow-hidden rounded border border-border bg-card', className)}>
      <div
        className={cn('overflow-x-auto', scrolls && 'overflow-y-auto')}
        style={scrolls ? { maxHeight: maxBodyHeight } : undefined}
      >
        <table className="w-full border-collapse text-[13px]">
          {/* Sticky only when the body scrolls: a header that outlives its rows is the point, but
              `position: sticky` on a header that never moves just invites paint bugs. */}
          <thead className={cn(scrolls && 'sticky top-0 z-10 bg-card')}>
            <tr>
              {columns.map(column => (
                <th
                  key={column.key}
                  className={cn(
                    'border-b border-border px-4 py-2.5 text-left font-normal whitespace-nowrap',
                    column.className
                  )}
                >
                  <MicroLabel size="xs">{column.header}</MicroLabel>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="px-4 py-10 text-center text-muted">
                  {empty}
                </td>
              </tr>
            ) : (
              rows.map(row => (
                <tr
                  key={rowKey(row)}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  className={cn(
                    'border-b border-border last:border-b-0',
                    onRowClick && 'cursor-pointer transition-colors hover:bg-raised'
                  )}
                >
                  {columns.map(column => (
                    <td key={column.key} className={cn('px-4 py-2.5 align-middle text-foreground', column.className)}>
                      {column.render(row)}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {footer ? <div className="border-t border-border px-4 py-3">{footer}</div> : null}
    </div>
  );
}
